<template>
    <div class="checkout-component">
        <form ref="checkoutForm" :action="action" :method="method" @submit.prevent="checkAndStore">
            <input type="hidden" name="_token" :value="csrf"/>
            <input type="hidden" name="amount" :value="amount"/>
            <input type="hidden" name="fee" :value="processingFee.amount"/>

            <slot name="before"></slot>

            <div class="form-row">
                <payone-client
                    ref="payoneClient"
                    v-bind:payment-method="localPaymentMethod"
                    v-bind:config="config.payone"
                    v-bind:values="values"
                    v-on:payment-method-changed="paymentMethodChanged"
                    :user="user"
                />
            </div>

            <div class="form-row" v-show="showCalculator">
                <rate-calculator ref="rateCalculator" :price="price" v-on:interestChange="interestChanged"
                                 v-on:installmentChange="installment = $event"
                                 :installment-type="config.installmentType"/>
            </div>

            <div v-show="this.config.installmentType === 'fixed_3'" class="mt-4">
                <p>
                    Dieser Kurs wird in 3 Raten bezahlt. Die Erste wird direkt während der Buchung fällig und online
                    bezahlt. Die zwei
                    verbleibenden werden dann jeweils mit einem Abstand von 6 Wochen fällig.
                </p>
            </div>

            <div v-if="!!bookableDates && bookableDates.length > 0">
                <course-date-picker :bookableDates="bookableDates"></course-date-picker>
            </div>

            <div class="card mt-3">
                <div class="card-header">
                    <h2>Zusammenfassung</h2>
                </div>
                <div class="card-body">
                    <totals :totals="totals"></totals>
                    <div class="form-check mb-4 mt-3">
                        <input name="terms-agreed" id="terms-check" class="form-check-input required" type="checkbox"
                               required/>
                        <label for="terms-check" class="form-check-label">Hiermit bestätige Ich, dass ich die
                            <a href="https://heilpferde.de/agb" target="_blank">AGB</a>
                            gelesen habe und damit einverstanden bin. </label>
                    </div>
                    <b-button id="pay"  ref="checkAndStoreBtn" :variant="'primary'" class="m-1" type="submit" block="block"
                              size="lg"
                              :disabled="submitDisabled">{{ $t('strings.frontend.checkout.pay') }}
                    </b-button>
                </div>
            </div>
            <slot name="after"></slot>
        </form>
    </div>
</template>
<script>
export default {
    props: {
        price: Number,
        config: Object,
        values: Object,
        action: String,
        method: String,
        paymentMethod: String,
        user: Object,
        bookableDates: Array
    },
    data: function () {
        return {
            localPaymentMethod: this.paymentMethod,
            submitDisabled: false,
            rateCalculator: this.$refs.rateCalculator,
            interest: 0,
            onetimePayment: false,
            installment: 0
        }
    },
    computed: {
        csrf: function () {
            return document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        },
        showCalculator: function () {
            if (this.config && this.config.hasOwnProperty('installmentType')) {
                if (this.config.installmentType === 'allowed') {
                    return true;
                }
                return !(this.config.installmentType === 'fixed_3' || 'none');
            } else {
                return false;
            }
        },
        amount: function () {
            if (this.installment > 0 && this.config.installmentType === 'allowed') {
                return this.installment;
            }
            if(this.config.installmentType === 'fixed_3'){
                return this.price / 3;
            } else {
                return this.price;
            }
        },
        totals: function () {
            let totals = [
                {
                    label: this.$t('strings.frontend.checkout.totals.grandtotal.excl'),
                    amount: this.grandTotal['excl']
                }];

            if (this.processingFee.amount > 0 && this.config.installmentType === 'allowed') {
                totals.push({
                    label: this.$t('strings.frontend.checkout.totals.processingfee') + ' ( ' + this.processingFee['feePercent'] + ' %)',
                    amount: this.processingFee['amount']
                });
            }

            if (this.config.vatPercent > 0) {
                totals.push({
                    label: this.$t('strings.frontend.checkout.totals.tax') + ' ( ' + this.config.vatPercent + '% )',
                    amount: this.calcTax
                });
            }

            totals.push({
                label: this.$t('strings.frontend.checkout.totals.grandtotal.incl'),
                amount: this.grandTotal['incl']
            });

            return totals;
        },
        grandTotal: function () {
            let grandTotalExclTax = 0;
            let grandTotalInclTax = this.price;

            if (this.showCalculator) {
                if (this.processingFee.amount > 0) {
                    grandTotalInclTax += this.processingFee['amount']
                } else {
                    grandTotalInclTax = this.price;
                }
            }

            grandTotalExclTax = (grandTotalInclTax / ((this.config.vatPercent / 100) + 1));

            return {
                'excl': grandTotalExclTax,
                'incl': grandTotalInclTax
            };
        },
        calcTax: function () {
            return this.grandTotal['incl'] - this.grandTotal['excl'];
        },
        processingFee: function () {
            if (this.interest > 0 && this.config.installmentType === 'allowed') {
                return {
                    'amount': (this.price * (this.interest / 100)),
                    'feePercent': this.interest
                }
            }

            return 0;
        }
    },
    methods: {
        checkAndStore: function (event) {
            const vueComponent = this;
            const timeoutMS = 2000;

            this.submitDisabled = true;

            this.$nextTick(() => {
                    // Create a promise that rejects in <ms> milliseconds
                    const timeout = new Promise((resolve, reject) => {
                        let id = setTimeout(() => {
                            clearTimeout(id);
                            reject('Timed out in ' + timeoutMS + 'ms.');
                        }, timeoutMS)
                    });

                    Promise.race([
                        vueComponent.$refs.payoneClient.checkAndStore(), timeout
                    ]).then((res) => {
                        if (res === true) {
                            vueComponent.$refs.checkoutForm.submit();
                        }
                    }).catch((err) => {
                        this.$log.error('Timeout', err);
                    }).finally((err) => {
                        this.$log.error(err);
                        vueComponent.submitDisabled = false;
                    });
                }
            );
            return false;
        },
        paymentMethodChanged: function (newMethod, oldMethod) {
            this.$log.info('Checkout', oldMethod, ' > ', newMethod);
            this.localPaymentMethod = newMethod;
        },
        interestChanged: function (newInterest) {
            this.interest = newInterest;
        }
    }
}
</script>
